import React, { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { resetPassword } from '../services/auth'
import { TicketIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const { userId, token } = useParams()
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const onSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')
    const res = await resetPassword({ userId, token, newPassword: password })
    setLoading(false)
    if (res.status === 200) {
      setMessage('Password reset successfully')
      setTimeout(() => navigate('/login'), 1200)
    } else {
      setError(res.error || 'Could not reset password')
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-slate-50 font-sans">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center items-center gap-2 mb-8 group">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-600 to-cyan-500 text-white shadow-lg transition-transform group-hover:scale-105">
            <TicketIcon className="h-6 w-6" />
          </div>
          <span className="text-2xl font-bold tracking-tight text-slate-900">Obule</span>
        </Link>
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900">
            Set new password
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Enter your new password below
          </p>
        </div>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white px-6 py-10 shadow-xl border border-slate-100 sm:rounded-2xl sm:px-12">
          <form className="space-y-6" onSubmit={onSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-100 flex items-start gap-3 text-red-700 font-medium text-sm">
                <p>{error}</p>
              </div>
            )}
            {message && (
              <div className="rounded-lg bg-green-50 p-4 border border-green-100 flex items-start gap-3 text-green-700 font-medium text-sm">
                <p>{message}</p>
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-semibold leading-6 text-slate-900">
                New password
              </label>
              <div className="relative mt-2">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <LockClosedIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                </div>
                <input
                  id="password"
                  name="newPassword"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="New strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-lg border-0 py-2.5 pl-10 pr-10 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm sm:leading-6 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <EyeIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center rounded-lg bg-cyan-600 px-3 py-2.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-cyan-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600 transition-all disabled:opacity-70 disabled:cursor-not-allowed hover:shadow-md hover:-translate-y-0.5 transform duration-200"
              >
                {loading ? 'Resetting...' : 'Reset password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage
